import { makeOptionallyAuthenticatedQuery } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { AllListingsListing } from '@b-stock/search-api-client'

import type { SearchResult } from './listingSearchQuery'
import { augmentListings } from './listingSearchQuery'

// The page HTML waits on this lookup when it runs on the server, so it is
// capped well under fe-core's 5 s server default. The browser is left uncapped,
// as fe-core leaves it: a slow phone would hit a tight cap on every load.
const SERVER_TIMEOUT_MS = 1500

/** A card can show a price only once its auction record has been found. */
export const isPriced = (listing: SearchResult) =>
  !!(listing.auction3MP ?? listing.auctionEnt)

/**
 * Adds the auction records a `ListingCard` prices from to listings the caller
 * already has, without a second search.
 */
export const getListingCards = async (
  accessToken: string | null,
  listings: AllListingsListing[]
): Promise<SearchResult[]> => {
  const onServer = typeof window === 'undefined'
  const signal = onServer ? AbortSignal.timeout(SERVER_TIMEOUT_MS) : undefined
  const results = await augmentListings(
    accessToken,
    listings,
    buildAxiosRequestConfig({ accessToken, signal })
  )
  // augmentListings turns each failed service call into an empty result.
  // Throwing when nothing came back keeps that failure out of the cache.
  const pricedCount = results.filter(isPriced).length
  if (!pricedCount) {
    throw new Error('No listing could be priced')
  }
  // A partial failure still succeeds and its cards are dropped, so say so.
  if (onServer && pricedCount < results.length) {
    console.warn(
      `Could not price ${results.length - pricedCount} of ${results.length} listing cards`
    )
  }
  return results
}

const listingCardsQuery = makeOptionallyAuthenticatedQuery<
  [AllListingsListing[]],
  SearchResult[]
>('listing-cards', getListingCards, {
  makeKey: (name, [accessToken, listings]) => [
    name,
    accessToken,
    listings.map((listing) => listing.id),
  ],
  staleTime: 5 * 60 * 1000, // 5 minutes
})

export default listingCardsQuery
